import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { statisticsApi } from '@/api';
import type { AggregatedRow, StatsQuery } from '@/api';
import { extractApiError, type ApiErrorInfo } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';
import { reportTimezone, type CabinetTimeUser } from '@/utils/cabinetTime';

export interface StatsBranch {
  rows: AggregatedRow[];
  totals: AggregatedRow | null;
  isLoading: boolean;
  /** WEB-1955: причина отказа — РАЗОБРАННАЯ (статус/код/текст), фразу человеку
   * выбирает экран через `localizeError`; текстом код терялся в редьюсере. */
  error: ApiErrorInfo | null;
  /**
   * WEB-1337 P5: requestId of the most recently DISPATCHED fetch for this
   * branch. Two different screens write into `general` (StatisticsTab's
   * "Общее" tab AND LeadsTab, both via fetchStatsThunk) plus filter changes
   * can fire overlapping requests — an older, slower request resolving
   * AFTER a newer one already landed must not clobber the fresher data with
   * stale rows. fulfilled/rejected only apply when their action's
   * meta.requestId still matches this field; otherwise the response is
   * discarded as out-of-order. Shared by construction across every caller
   * of the same thunk, since they all write the same branch.
   */
  latestRequestId: string | null;
  /**
   * WEB-1812 — граница честного счёта из ответа: с какой даты отметки
   * quiz50/quiz90 считаются по фактическому пути посетителя. Печатает её
   * таблица статистики; своей копии даты у фронта нет (одна правда — бэк).
   * null — ответа ещё не было либо он прежнего формата.
   */
  pathProgressSince: string | null;
}

export interface StatisticsState {
  general: StatsBranch;
  daily: StatsBranch;
  /**
   * Ф2 (ревизия десятки 3, 15.08): LeadsTab used to write into `general`
   * through fetchStatsThunk too (fixed 30-day query, for the metrics-overview
   * chips) — the SAME branch StatisticsTab's "Общее" tab writes into with the
   * user's own filters. With both tabs mounted (antd Tabs, no
   * destroyInactiveTabPane) a project switch fires both dispatches; the
   * requestId guard correctly picks the LAST-landed one, but "last" could be
   * LeadsTab's fixed query — "Общее" then deterministically shows rows for
   * the wrong (leads) params. A dedicated branch removes the collision by
   * construction: each screen's dispatch can only ever affect its own rows.
   */
  leadsGeneral: StatsBranch;
}

const emptyBranch: StatsBranch = {
  rows: [],
  totals: null,
  isLoading: false,
  error: null,
  latestRequestId: null,
  pathProgressSince: null,
};

const initialState: StatisticsState = {
  general: { ...emptyBranch },
  daily: { ...emptyBranch },
  leadsGeneral: { ...emptyBranch },
};

export const fetchStatsThunk = createAsyncThunk(
  'statistics/fetchStats',
  async (query: StatsQuery, { rejectWithValue }) => {
    try {
      const response = await statisticsApi.stats(query);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to fetch statistics');
    }
  }
);

/**
 * Ф2: same API call as fetchStatsThunk (statisticsApi.stats) — a separate
 * thunk purely so its pending/fulfilled/rejected write into the `leadsGeneral`
 * branch instead of `general`. LeadsTab dispatches THIS thunk; StatisticsTab
 * keeps dispatching fetchStatsThunk into `general`. See StatisticsState
 * doc-comment above for the collision this separates.
 */
export const fetchLeadsStatsThunk = createAsyncThunk(
  'statistics/fetchLeadsStats',
  async (query: StatsQuery, { rejectWithValue }) => {
    try {
      const response = await statisticsApi.stats(query);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to fetch statistics');
    }
  }
);

export const fetchDailyThunk = createAsyncThunk(
  'statistics/fetchDaily',
  async (query: StatsQuery, { rejectWithValue, getState }) => {
    try {
      // WEB-2404 пп.3, 9: сутки «По дням» — в поясе отчёта. Пояс кладёт сам
      // thunk из профиля через одну дверь reportTimezone: любой, кто зовёт
      // «По дням», шлёт tz (сторож reportTimezone.twoEnds.web2404).
      const auth = (getState() as { auth?: { user?: CabinetTimeUser | null } }).auth;
      const response = await statisticsApi.daily({
        ...query,
        tz: reportTimezone(auth?.user ?? null),
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to fetch daily statistics');
    }
  }
);

const statisticsSlice = createSlice({
  name: 'statistics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // General tab branch
      .addCase(fetchStatsThunk.pending, (state, action) => ({
        ...state,
        general: {
          ...state.general,
          isLoading: true,
          error: null,
          // WEB-1337 P5: clear stale rows the moment a new fetch starts —
          // otherwise a slow/failed refetch leaves the PREVIOUS filter's
          // rows on screen looking current while loading/erroring.
          rows: [],
          // Ф4а: totals must clear alongside rows (symmetry with rejected,
          // which already clears both) — otherwise the PREVIOUS filter's
          // totals row lingers next to an empty/loading table looking current.
          totals: null,
          latestRequestId: action.meta?.requestId ?? null,
        },
      }))
      .addCase(fetchStatsThunk.fulfilled, (state, action) => {
        // Out-of-order guard: only the LATEST dispatched request for this
        // branch may write — an older request resolving late is discarded.
        if (action.meta.requestId !== state.general.latestRequestId) return state;
        return {
          ...state,
          general: {
            rows: action.payload.rows,
            totals: action.payload.totals,
            isLoading: false,
            error: null,
            latestRequestId: state.general.latestRequestId,
            // WEB-1812: границу честного счёта несёт КАЖДАЯ ветка — сводка,
            // дневная разбивка и лиды смотрят на одни и те же отметки, и
            // довести пометку до одной таблицы из трёх значило бы оставить
            // две врать молчанием.
            pathProgressSince: action.payload.pathProgressSince ?? null,
          },
        };
      })
      .addCase(fetchStatsThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.general.latestRequestId) return state;
        return {
          ...state,
          general: {
            ...state.general,
            isLoading: false,
            error: rejectedApiError(action),
            // WEB-1337 P2: don't leave stale rows/totals next to the error.
            rows: [],
            totals: null,
          },
        };
      })
      // Daily tab branch
      .addCase(fetchDailyThunk.pending, (state, action) => ({
        ...state,
        daily: {
          ...state.daily,
          isLoading: true,
          error: null,
          rows: [],
          // Ф4а: см. общий branch выше — та же симметрия с rejected.
          totals: null,
          latestRequestId: action.meta?.requestId ?? null,
        },
      }))
      .addCase(fetchDailyThunk.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.daily.latestRequestId) return state;
        return {
          ...state,
          daily: {
            rows: action.payload.rows,
            totals: action.payload.totals,
            isLoading: false,
            error: null,
            latestRequestId: state.daily.latestRequestId,
            // WEB-1812: границу честного счёта несёт КАЖДАЯ ветка — сводка,
            // дневная разбивка и лиды смотрят на одни и те же отметки, и
            // довести пометку до одной таблицы из трёх значило бы оставить
            // две врать молчанием.
            pathProgressSince: action.payload.pathProgressSince ?? null,
          },
        };
      })
      .addCase(fetchDailyThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.daily.latestRequestId) return state;
        return {
          ...state,
          daily: {
            ...state.daily,
            isLoading: false,
            error: rejectedApiError(action),
            rows: [],
            totals: null,
          },
        };
      })
      // Ф2: leadsGeneral branch (LeadsTab) — mirrors general/daily above,
      // including the WEB-1337 P5 requestId out-of-order guard.
      .addCase(fetchLeadsStatsThunk.pending, (state, action) => ({
        ...state,
        leadsGeneral: {
          ...state.leadsGeneral,
          isLoading: true,
          error: null,
          rows: [],
          totals: null,
          latestRequestId: action.meta?.requestId ?? null,
        },
      }))
      .addCase(fetchLeadsStatsThunk.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.leadsGeneral.latestRequestId) return state;
        return {
          ...state,
          leadsGeneral: {
            rows: action.payload.rows,
            totals: action.payload.totals,
            isLoading: false,
            error: null,
            latestRequestId: state.leadsGeneral.latestRequestId,
            // WEB-1812: границу честного счёта несёт КАЖДАЯ ветка — сводка,
            // дневная разбивка и лиды смотрят на одни и те же отметки, и
            // довести пометку до одной таблицы из трёх значило бы оставить
            // две врать молчанием.
            pathProgressSince: action.payload.pathProgressSince ?? null,
          },
        };
      })
      .addCase(fetchLeadsStatsThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.leadsGeneral.latestRequestId) return state;
        return {
          ...state,
          leadsGeneral: {
            ...state.leadsGeneral,
            isLoading: false,
            error: rejectedApiError(action),
            rows: [],
            totals: null,
          },
        };
      });
  },
});

export default statisticsSlice.reducer;
